import React from "react";
import cs from "classnames";
import { Icon } from "../Icon/Icon";
import {
  getPasswordRequirements,
  UserData,
  MIN_LENGTH,
  MIN_TYPES,
  SPECIAL_CHAR_SET,
} from "../../../lib/utils/passwordValidation";
import "./PasswordRequirements.scss";

interface Props {
  password?: string;
  userData?: UserData;
}

export const PasswordRequirements = ({ password = "", userData }: Props) => {
  const reqs = getPasswordRequirements(password, userData);

  // Helper to determine if we should show success or neutral state
  // We show success (green) if met.
  // We show neutral/grey if not met yet.
  // We can opt to show red if the user has typed enough characters but missed it, 
  // but for "live checklist" usually grey -> green is standard.

  const renderItem = (isValid: boolean, text: string, isSubItem = false) => (
    <li
      className={cs("PasswordRequirement", {
        "PasswordRequirement--valid": isValid,
        "PasswordRequirement--sub": isSubItem,
      })}
    >
      <div className="PasswordRequirement__icon">
        {isValid ? (
          <Icon icon="check-icon" className="text-success" />
        ) : (
          // Using a simple circle or empty checkbox for unmet
          <Icon icon="checkbox-empty" className="text-muted" />
        )}
      </div>
      <span>{text}</span>
    </li>
  );

  return (
    <div className="PasswordRequirements">
      <p className="PasswordRequirements__title">Password requirements:</p>
      <ul className="PasswordRequirements__list">
        {renderItem(reqs.length, `Minimum length: ${MIN_LENGTH} characters`)}
        {renderItem(
          reqs.typesCount === MIN_TYPES,
          `Must include all ${MIN_TYPES} of the following character types:`
        )}
        <ul className="PasswordRequirements__sublist">
          {renderItem(reqs.types.uppercase, "Uppercase letters (A–Z)", true)}
          {renderItem(reqs.types.lowercase, "Lowercase letters (a–z)", true)}
          {renderItem(reqs.types.digits, "Digits (0–9)", true)}
          {renderItem(
            reqs.types.special,
            `Special characters (allowed: ${SPECIAL_CHAR_SET})`,
            true
          )}
        </ul>
        {userData &&
          renderItem(
            reqs.noUserInfo,
            "Password must not contain parts of your name or email"
          )}
      </ul>
    </div>
  );
};
